import { Component, OnDestroy, OnInit } from '@angular/core';
import { SubscriptionsManager } from '../../../shared/util/subscriptions-manager';
import { JhiEventManager, JhiLanguageService } from '../../../shared/core-utils';
import { VariableService } from '../../../shared/ontology/service/variable.service';
import { ParamContext } from '../../../shared/service/param.context';
import { SORT_PREDICATE_NONE } from '../../../germplasm-manager/germplasm-search-resolve-paging-params';
import { ColumnFilterComponent, FilterType } from '../../../shared/column-filter/column-filter.component';
import { ColumnFilterTransitionEventModel } from '../../../shared/column-filter/column-filter-transition-event.model';
import { MatchType } from '../../../shared/column-filter/column-filter-text-with-match-options-component';
import { VariableSearchRequest } from '../../../shared/ontology/model/variable-search-request.model';
import { finalize } from 'rxjs/internal/operators/finalize';
import { HttpResponse } from '@angular/common/http';
import { Variable } from '../../../shared/ontology/model/variable';
import { ColumnFilterRadioButtonOption } from '../../../shared/column-filter/column-filter-radio-component';
import { ProgramService } from '../../../shared/program/service/program.service';
import { ProgramFavorite } from '../../../shared/program/model/program-favorite.model';
import { ProgramFavoriteRequest } from '../../../shared/program/model/program-favorite-add-request.model';
import { ProgramFavoriteTypeEnum } from '../../../shared/program/model/program-favorite-type.enum';
import { OntologyService } from '../../../shared/ontology/service/ontology.service';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

export enum VariableTypeEnum {
    ANALYSIS = 'ANALYSIS',
    ENVIRONMENT_CONDITION = 'ENVIRONMENT_CONDITION',
    GERMPLASM_DESCRIPTOR = 'GERMPLASM_DESCRIPTOR',
    STUDY_DETAIL = 'STUDY_DETAIL',
    ENVIRONMENT_DETAIL = 'ENVIRONMENT_DETAIL',
    SELECTION_METHOD = 'SELECTION_METHOD',
    TRAIT = 'TRAIT',
    TREATMENT_FACTOR = 'TREATMENT_FACTOR',
    EXPERIMENTAL_DESIGN = 'EXPERIMENTAL_DESIGN',
    ANALYSIS_SUMMARY = 'ANALYSIS_SUMMARY',
    OBSERVATION_UNIT = 'OBSERVATION_UNIT',
    GERMPLASM_PASSPORT = 'GERMPLASM_PASSPORT',
    GERMPLASM_ATTRIBUTE = 'GERMPLASM_ATTRIBUTE',
    ENTRY_DETAIL = 'ENTRY_DETAIL',
    INVENTORY_ATTRIBUTE = 'INVENTORY_ATTRIBUTE',
    GENOTYPE_MARKER = 'GENOTYPE_MARKER',
    INVENTORY_UNIT = 'INVENTORY_UNIT'
}

export const VARIABLE_TYPE_MAP: Record<VariableTypeEnum, VariableType> = {
    [VariableTypeEnum.ANALYSIS]: {
        id: 1801,
        name: 'Analysis',
        description: 'Variable to be used only in analysis (for example derived variables).'
    },
    [VariableTypeEnum.ENVIRONMENT_CONDITION]: {
        id: 1802,
        name: 'Environment Condition',
        description: 'Observations made of conditions in an individual environment involved in a study.'
    },
    [VariableTypeEnum.GERMPLASM_DESCRIPTOR]: {
        id: 1804,
        name: 'Germplasm Descriptor',
        description: 'Information to be recorded about each germplasm in a study.'
    },
    [VariableTypeEnum.STUDY_DETAIL]: {
        id: 1805,
        name: 'Study Detail',
        description: 'Administrative details to be tracked per study.'
    },
    [VariableTypeEnum.ENVIRONMENT_DETAIL]: {
        id: 1806,
        name: 'Environment Detail',
        description: 'Administrative details to be tracked per environment.'
    },
    [VariableTypeEnum.SELECTION_METHOD]: {
        id: 1807,
        name: 'Selection Method',
        description: 'How material is chosen for advancing to the next generation.'
    },
    [VariableTypeEnum.TRAIT]: {
        id: 1808,
        name: 'Trait',
        description: 'Characteristics of a germplasm to be recorded during a study.'
    },
    [VariableTypeEnum.TREATMENT_FACTOR]: {
        id: 1809,
        name: 'Treatment Factor',
        description: 'Treatments to be applied to members of a trial.'
    },
    [VariableTypeEnum.EXPERIMENTAL_DESIGN]: {
        id: 1810,
        name: 'Experimental Design',
        description: 'Experimental Design Variables'
    },
    [VariableTypeEnum.ANALYSIS_SUMMARY]: {
        id: 1811,
        name: 'Analysis Summary',
        description: 'Analysis Summary Variables'
    },
    [VariableTypeEnum.OBSERVATION_UNIT]: {
        id: 1812,
        name: 'Observation Unit',
        description: 'Observation Unit Variables'
    },
    [VariableTypeEnum.GERMPLASM_PASSPORT]: {
        id: 1813,
        name: 'Germplasm Passport',
        description: 'Germplasm Passport'
    },
    [VariableTypeEnum.GERMPLASM_ATTRIBUTE]: {
        id: 1814,
        name: 'Germplasm Attribute',
        description: 'Germplasm Attribute'
    },
    [VariableTypeEnum.ENTRY_DETAIL]: {
        id: 1815,
        name: 'Entry Detail',
        description: 'Variables that describes list entries'
    },
    [VariableTypeEnum.INVENTORY_ATTRIBUTE]: {
        id: 1816,
        name: 'Inventory Attribute',
        description: 'Inventory Attribute'
    },
    [VariableTypeEnum.GENOTYPE_MARKER]: {
        id: 1817,
        name: 'Genotype Marker',
        description: 'Genotype Marker'
    },
    [VariableTypeEnum.INVENTORY_UNIT]: {
        id: 1818,
        name: 'Inventory Unit',
        description: 'Inventory Unit'
    }
};

@Component({
    selector: 'jhi-variables-pane',
    templateUrl: 'variables-pane.component.html'
})
export class VariablesPaneComponent implements OnInit, OnDestroy {
    static readonly COLUMN_FILTER_EVENT_NAME = 'searchColumnFiltersChanged';
    readonly VARIABLE_TYPE_MAP = VARIABLE_TYPE_MAP;

    COLUMN_FILTER_EVENT_NAME = VariablesPaneComponent.COLUMN_FILTER_EVENT_NAME;
    itemsPerPage = 20;

    ColumnLabels = ColumnLabels;

    variables: Variable[];
    searchRequest: VariableSearchRequest;
    subscriptionsManager: SubscriptionsManager = new SubscriptionsManager();

    currentSearch: string;
    totalItems: number;
    page: number;
    previousPage: number;
    predicate: any;
    reverse: boolean;

    isLoading: boolean;

    variableFilters: any;

    isProgramFavoriteFilterApplied: boolean;

    isTogglingFavoriteProgram: boolean;


    constructor(private activatedRoute: ActivatedRoute,
                private router: Router,
                private jhiLanguageService: JhiLanguageService,
                private eventManager: JhiEventManager,
                private variableService: VariableService,
                private programService: ProgramService,
                private ontologyService: OntologyService,
                private context: ParamContext,
                private translateService: TranslateService) {
        this.page = 1;
        this.totalItems = 0;
        this.currentSearch = '';
        this.predicate = ColumnLabels.NAME;
        this.reverse = true;
        this.searchRequest = new VariableSearchRequest();
    }

    get filters() {
        return this.variableFilters;
    }

    set filters(filters) {
        this.variableFilters = filters;
    }

    get request() {
        return this.searchRequest;
    }

    ngOnDestroy(): void {
        this.subscriptionsManager.unsubscribeAll();
    }

    ngOnInit(): void {
        this.filters = this.getInitialFilters();
        this.request.showObsoletes = 0;
        this.request.programUUID = this.context.programUUID;
        this.registerColumnFiltersChanged();
        this.registerEvents();
        this.loadAll(this.request);
    }

    registerEvents(): any {
        this.subscriptionsManager.add = this.eventManager.subscribe('variablePaneViewChanged', (event) => {
            if (event?.content?.filterBy) {
                this.filters = this.getInitialFilters();
                this.searchRequest = new VariableSearchRequest();
                this.request.showObsoletes = 0;
                this.request.programUUID = this.context.programUUID;
                this.request.variableTypeIds = event.content.values.map(type => type.id);
                this.filters.forEach(filter => {
                    if (filter.key === event.content.filterBy) {
                        filter.added = true;
                        filter.value = event.content.values;
                        this.eventManager.broadcast({ name: 'reloadFilters', content: { filters: this.filters, request: this.request } });
                    }
                });
            }
            this.loadAll(this.request);
        });
    }

    resetTable() {
        this.page = 1;
        this.previousPage = 1;
        this.loadAll(this.request);
    }

    loadPage(page: number) {
        if (page !== this.previousPage) {
            this.previousPage = page;
            this.transition();
        }
    }

    transition() {
        this.router.navigate(['./'], {
            queryParamsHandling: 'merge',
            queryParams: {
                page: this.page,
                size: this.itemsPerPage,
                search: this.currentSearch,
                sort: this.getSort()
            },
            relativeTo: this.activatedRoute
        });
        this.loadAll(this.request);
    }

    getSort() {
        if (this.predicate === SORT_PREDICATE_NONE) {
            return '';
        }
        return [this.predicate + ',' + (this.reverse ? 'asc' : 'desc')];
    }

    loadAll(request: VariableSearchRequest) {
        this.isLoading = true;
        this.variableService.searchVariablesOntology(request,
            {
                page: this.page - 1,
                size: this.itemsPerPage,
                sort: this.getSort()
            }
        ).pipe(finalize(() => {
            this.isLoading = false;
        })).subscribe(
            (res: HttpResponse<Variable[]>) => this.onSuccess(res.body, res.headers)
        );
    }

    onClearSort($event) {
        $event.preventDefault();
        this.clearSort();
        this.transition();
    }

    trackId(index: number, item: Variable) {
        return item.id;
    }

    toggleProgramFavorite(variable: Variable) {
        // Prevent multiple click while a program favorite is being added/removed
        if (this.isTogglingFavoriteProgram) {
            return;
        }

        this.isTogglingFavoriteProgram = true;
        variable.isFavorite = !variable.isFavorite;
        if (variable.isFavorite) {
            this.programService.addProgramFavorite(
                new ProgramFavoriteRequest(ProgramFavoriteTypeEnum.VARIABLE, [parseInt(variable.id, 10)]))
                .pipe(finalize(() => {
                    this.isTogglingFavoriteProgram = false;
                    this.loadAll(this.request);
                })).subscribe();
        } else {
            this.programService.getProgramFavorite(ProgramFavoriteTypeEnum.VARIABLE, parseInt(variable.id, 10))
                .subscribe((res: HttpResponse<ProgramFavorite>) => {
                    if (res?.body?.programFavoriteId) {
                        this.programService.removeProgramFavorite([res.body.programFavoriteId])
                            .pipe(
                                finalize(() => {
                                    this.isTogglingFavoriteProgram = false;
                                    this.loadAll(this.request);
                                })
                            ).subscribe();
                    }
                });
        }
    }

    isProgramFavorite(variable: Variable): boolean {
        return variable.isFavorite;
    }

    private registerColumnFiltersChanged() {
        this.subscriptionsManager.add = this.eventManager.subscribe(VariablesPaneComponent.COLUMN_FILTER_EVENT_NAME, (event: ColumnFilterTransitionEventModel) => {
            this.resetTable();
        });
    }

    private getInitialFilters() {
        return [
            {
                key: 'showObsoletes', name: 'column-filter.obsolete', default: true, defaultValue: 0, type: FilterType.RADIOBUTTON, options: this.getIsObsoleteFilterOptions(),
                transform(req) {
                    req[this.key] = this.value;
                }
            }, {
                key: 'nameFilter', name: 'column-filter.variable.name', placeholder: 'column-filter.search.text.placeholder', type: FilterType.TEXT_WITH_MATCH_OPTIONS,
                matchType: MatchType.STARTSWITH, default: true
            }, {
                key: 'nameOrAlias', name: 'column-filter.variable.alias.name', placeholder: 'column-filter.match.text.placeholder', type: FilterType.TEXT
            }, {
                key: 'programUUIDFilter', name: 'column-filter.program.favorite', default: true, defaultValue: false, type: FilterType.RADIOBUTTON, options: this.getVariableFavoriteFilterOptions(),
                transform(req) {
                    req[this.key] = this.value;
                },
            }, {
                key: 'variableTypeIds', name: 'column-filter.variable.type', type: FilterType.DROPDOWN, values: this.getVariableTypeOptions(), multipleSelect: true,
                transform(req) {
                    ColumnFilterComponent.transformDropdownFilter(this, req);
                },
                reset(req) {
                    ColumnFilterComponent.resetDropdownFilter(this, req);
                },
            }, {
                key: 'createdDate', name: 'column-filter.created.date', type: FilterType.DATE,
                fromKey: 'createdDateFrom',
                toKey: 'createdDateTo',
                transform(req) {
                    ColumnFilterComponent.transformDateFilter(this, req, this.fromKey, this.toKey);
                },
                reset(req) {
                    ColumnFilterComponent.resetRangeFilter(this, req, this.fromKey, this.toKey);
                },
                reload(req) {
                    this.from = req[this.fromKey];
                    this.to = req[this.toKey];
                }
            },  {
                key: 'propertyIds', name: 'column-filter.properties', placeholder: 'column-filter.properties.placeholder', type: FilterType.PROPERTIES, properties: [],
                transform(req) {
                    ColumnFilterComponent.transformIdCollectionFilter(this, req, 'properties');
                },
                reset(req) {
                    ColumnFilterComponent.resetIdCollectionFilter(this, req, 'properties');
                },
            },  {
                key: 'methodIds', name: 'column-filter.methods', placeholder: 'column-filter.methods.placeholder', type: FilterType.METHODS, methods: [],
                transform(req) {
                    ColumnFilterComponent.transformIdCollectionFilter(this, req, 'methods');
                },
                reset(req) {
                    ColumnFilterComponent.resetIdCollectionFilter(this, req, 'methods');
                },
            },  {
                key: 'scaleIds', name: 'column-filter.scales', placeholder: 'column-filter.scales.placeholder', type: FilterType.SCALES, scales: [],
                transform(req) {
                    ColumnFilterComponent.transformIdCollectionFilter(this, req, 'scales');
                },
                reset(req) {
                    ColumnFilterComponent.resetIdCollectionFilter(this, req, 'scales');;
                },
            },
        ];
    }

    private async getVariableFavoriteFilterOptions(): Promise<ColumnFilterRadioButtonOption[]> {
        await this.translateService.get('no').toPromise();
        return new Promise<ColumnFilterRadioButtonOption[]>((resolve, reject) => {
            resolve([
                new ColumnFilterRadioButtonOption(true, 'yes'),
                new ColumnFilterRadioButtonOption(false, 'no')]);
        });
    }

    private async getIsObsoleteFilterOptions(): Promise<ColumnFilterRadioButtonOption[]> {
        await this.translateService.get('no').toPromise();
        return new Promise<ColumnFilterRadioButtonOption[]>((resolve, reject) => {
            resolve([new ColumnFilterRadioButtonOption(1, 'yes'),
                new ColumnFilterRadioButtonOption(0, 'no')]);
        });
    }

    private clearSort() {
        this.predicate = ColumnLabels.NAME;
        this.reverse = true;
    }

    private onSuccess(data: Variable[], headers) {
        this.totalItems = headers.get('X-Total-Count');
        this.variables = data;
    }

    private async getVariableTypeOptions() {
        const excludeRestrictedTypes = false;
        const variableType = await this.ontologyService.getVariableTypes(excludeRestrictedTypes).toPromise();
        return variableType.map((variableType) => {
            return {
                id: variableType.id,
                text: variableType.name
            };
        });
    }

    protected formatViewValue(variableTypes: any[] | undefined) {
        if (!variableTypes || variableTypes.length === 0) {
            return '-';
        }
        return variableTypes.map(x => x.name).join(', ');
    }

    protected formatVariableTypeValues(variableTypes: any[] | undefined): string {
        if (!variableTypes || variableTypes.length === 0) {
            return '-';
        }

        return variableTypes
            .map(type => {
                return this.VARIABLE_TYPE_MAP[type]?.name;
            })
            .filter(name => !!name)
            .join(', ');
    }
}

export enum ColumnLabels {
    'NAME' = 'variableName',
    'ALIAS' = 'alias',
    'VARIABLE_TYPE' = 'variableTypeId',
    'PROPERTY' = 'propertyName',
    'METHOD' = 'methodName',
    'SCALE' = 'scaleName',
    'FAVORITE' = 'favoriteId',
}

export interface VariableType {
    id: number;
    name: string;
    description: string;
}
